import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdClose, MdContentCopy, MdLink } from 'react-icons/md'
import { createShare, type StorageFile } from '@/lib/api/storage/storage-api'

interface Props {
    isOpen: boolean
    file: StorageFile | null
    onClose: () => void
}

/**
 * Geração de link público.
 *
 * O link sai do perímetro autenticado, então os limites são definidos aqui:
 * validade, senha e teto de downloads. O padrão é conservador (7 dias) porque
 * link de laudo sem expiração vira exposição permanente de dado de saúde.
 */
export default function StorageShareModal({ isOpen, file, onClose }: Props) {
    const [password, setPassword] = useState('')
    const [expiresInDays, setExpiresInDays] = useState(7)
    const [maxDownloads, setMaxDownloads] = useState('')
    const [allowDownload, setAllowDownload] = useState(true)
    const [creating, setCreating] = useState(false)
    const [link, setLink] = useState('')

    useEffect(() => {
        if (isOpen) {
            setPassword('')
            setExpiresInDays(7)
            setMaxDownloads('')
            setAllowDownload(true)
            setLink('')
        }
    }, [isOpen])

    if (!isOpen || !file) return null

    const handleCreate = async () => {
        setCreating(true)
        const response = await createShare({
            fileId: file.id,
            password: password || undefined,
            expiresInDays,
            maxDownloads: maxDownloads ? Number(maxDownloads) : undefined,
            allowDownload,
        })
        setCreating(false)

        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível criar o link')
            return
        }

        setLink(`${window.location.origin}/compartilhado/${response.result.token}`)
        toast.success('Link criado com sucesso')
    }

    const handleCopy = async () => {
        await navigator.clipboard.writeText(link)
        toast.success('Link copiado')
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <MdLink className="text-xl text-primary" />
                        Compartilhar por link
                    </h3>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={onClose}
                    >
                        <MdClose className="text-xl" />
                    </button>
                </div>

                <p className="text-sm text-base-content/70 mb-5 truncate">{file.name}</p>

                {link ? (
                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="storage-share-link">
                                <span className="label-text font-medium">Link gerado</span>
                            </label>
                            <div className="join w-full">
                                <input
                                    id="storage-share-link"
                                    type="text"
                                    className="input input-bordered join-item w-full font-mono text-xs"
                                    value={link}
                                    readOnly
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary join-item"
                                    onClick={handleCopy}
                                >
                                    <MdContentCopy />
                                    Copiar
                                </button>
                            </div>
                        </div>

                        <div className="alert alert-info py-2">
                            <span className="text-sm">
                                Qualquer pessoa com este link poderá acessar o arquivo dentro
                                das regras definidas. Você pode revogá-lo a qualquer momento em
                                Compartilhamentos.
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label pb-1.5" htmlFor="storage-share-expiry">
                                    <span className="label-text font-medium">Validade</span>
                                </label>
                                <select
                                    id="storage-share-expiry"
                                    className="select select-bordered w-full"
                                    value={expiresInDays}
                                    onChange={event =>
                                        setExpiresInDays(Number(event.target.value))
                                    }
                                >
                                    <option value={1}>1 dia</option>
                                    <option value={7}>7 dias</option>
                                    <option value={15}>15 dias</option>
                                    <option value={30}>30 dias</option>
                                    <option value={0}>Sem expiração</option>
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label pb-1.5" htmlFor="storage-share-max">
                                    <span className="label-text font-medium">
                                        Limite de downloads
                                    </span>
                                    <span className="label-text-alt text-base-content/50">
                                        Opcional
                                    </span>
                                </label>
                                <input
                                    id="storage-share-max"
                                    type="number"
                                    min={1}
                                    className="input input-bordered w-full"
                                    value={maxDownloads}
                                    placeholder="Sem limite"
                                    onChange={event => setMaxDownloads(event.target.value)}
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="storage-share-password">
                                <span className="label-text font-medium">Senha do link</span>
                                <span className="label-text-alt text-base-content/50">
                                    Opcional
                                </span>
                            </label>
                            <input
                                id="storage-share-password"
                                type="text"
                                className="input input-bordered w-full"
                                value={password}
                                placeholder="Exigida para abrir o arquivo"
                                onChange={event => setPassword(event.target.value)}
                            />
                        </div>

                        <label className="label cursor-pointer justify-start gap-3 py-1">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-primary checkbox-sm"
                                checked={allowDownload}
                                onChange={event => setAllowDownload(event.target.checked)}
                            />
                            <span className="label-text">
                                Permitir baixar o arquivo (desmarcado, apenas visualização)
                            </span>
                        </label>
                    </div>
                )}

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        {link ? 'Fechar' : 'Cancelar'}
                    </button>
                    {!link && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleCreate}
                            disabled={creating}
                        >
                            {creating && <span className="loading loading-spinner loading-sm" />}
                            Gerar link
                        </button>
                    )}
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
